import { createTickClock } from "./tickClock";
import { easeOutProgress, isRunning, interpolate } from "./transition";

const OHLC_FIELDS = ["open", "high", "low", "close"];

const isSameOhlc = (first, second) => OHLC_FIELDS.every((field) => first[field] === second[field]);

// All four prices move with one progress, so high stays above open / close and low below them
const interpolateBar = (from, to, progress) => {
  const bar = { ...to };

  OHLC_FIELDS.forEach((field) => {
    bar[field] = interpolate(from[field], to[field], progress);
  });

  return bar;
};

// A new bar grows out of its open
const flatBar = (bar) => ({ ...bar, high: bar.open, low: bar.open, close: bar.open });

/**
 * Bars as they are shown: a copy of the stored bars whose last bar moves
 * to the latest tick instead of jumping (E5). A new tick starts from the
 * value shown at that moment; the duration follows the tick rate (tickClock).
 * The copy is made only when the bars change as a whole; a frame replaces
 * only its last element, the stored bars are never changed.
 */
export const createLastBarMotion = () => {
  const clock = createTickClock();
  let bars = [];
  // { from, to, startTime, duration } of the last bar
  let transition = null;

  const shownLastBar = (now) =>
    transition
      ? interpolateBar(transition.from, transition.to, easeOutProgress(transition, now))
      : bars[bars.length - 1];

  const start = (from, to, now) => {
    transition = { from, to, startTime: now, duration: clock.record(now) };
  };

  return {
    /** Shown bars, oldest first. */
    get bars() {
      return bars;
    },

    isActive: (now) => isRunning(transition, now),

    /**
     * The bars changed as a whole: older history, a new bar or a reload.
     * A running animation of the same last bar goes on.
     *
     * @param {object[]} storeBars
     * @param {{ isNewBar: boolean, isAnimated: boolean, now: number }} params
     * @returns {object[]} shown bars
     */
    setBars: (storeBars, { isNewBar, isAnimated, now }) => {
      const lastIndex = storeBars.length - 1;
      const target = storeBars[lastIndex];

      bars = storeBars.slice();

      if (!target || !isAnimated) transition = null;
      else if (isNewBar) start(flatBar(target), target, now);
      else if (transition?.to !== target) transition = null;

      if (transition) bars[lastIndex] = shownLastBar(now);

      return bars;
    },

    /**
     * A tick changed the last stored bar (same length as the shown bars).
     *
     * @param {object[]} storeBars
     * @param {{ isAnimated: boolean, now: number }} params
     */
    updateLastBar: (storeBars, { isAnimated, now }) => {
      const lastIndex = bars.length - 1;
      const target = storeBars[lastIndex];
      const shown = shownLastBar(now);

      if (!isAnimated) {
        transition = null;
        bars[lastIndex] = target;
        return;
      }

      // A tick without a price change keeps the running animation and its timing
      if (isSameOhlc(transition ? transition.to : shown, target)) {
        if (transition) transition.to = target;
        else bars[lastIndex] = target;
        return;
      }

      start(shown, target, now);
      bars[lastIndex] = shown;
    },

    /**
     * Moves the last bar for a frame.
     *
     * @param {number} now - ms
     * @returns {boolean} true when the last bar changed
     */
    step: (now) => {
      if (!transition) return false;

      bars[bars.length - 1] = shownLastBar(now);

      if (!isRunning(transition, now)) transition = null;

      return true;
    },

    /**
     * Jumps to the latest tick (hidden tab, reduced motion).
     *
     * @returns {boolean} true when the last bar changed
     */
    finish: () => {
      clock.restart();

      if (!transition) return false;

      bars[bars.length - 1] = transition.to;
      transition = null;

      return true;
    },
  };
};
